import { isKnownError } from "api/api";
import {
  requestOtp,
  requestOtpEnc,
  verifyOtp,
  verifyOtpEnc,
} from "api/services/Form";
import BackButton from "components/BackButton/BackButton";
import Button from "components/Button";
import InputField from "components/InputField";
import Popup from "components/Popup";
import StepIndicator from "components/StepIndicator";
import { useSecSession } from "context/SecSessionContext";
import { RequestOtpRes, VerifyOtpRes } from "entities/Form";
import i18n from "i18n/i18n";
import { FC, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  constructSecurePayload,
  getApiLangParam,
  getOtpExpireMessage,
} from "utils/helper";
import { ReactComponent as IconSend } from "../../assets/send.svg";
import { ERROR_CODE, KnownError, POPUP_STATUS } from "../../entities";
import styles from "./Validation.module.scss";

interface FormProps {
  type: "create" | "edit";
  onSubmit: (otpSid: string, onError: () => void) => void;
  onBack: () => void;
  countryCode: string;
  mobileNo: string;
}

const Validation: FC<PropsWithChildren<FormProps>> = ({
  type,
  onSubmit,
  onBack,
  countryCode,
  mobileNo,
}) => {
  const { t } = useTranslation();
  const { secSession } = useSecSession();
  const [otp, setOtp] = useState<string>("");
  const [otpVerifying, setOTPVerifying] = useState<boolean | undefined>(
    undefined
  );
  const [requestedOTP, setRequestedOTP] = useState(false);
  const [requestOtpRes, setRequestOtpRes] = useState<RequestOtpRes>();
  const [sid, setSid] = useState("");
  const [secondsLeft, setSecondsLeft] = useState<number>();
  const [resendSecondsLeft, setResendSecondsLeft] = useState<number>();
  const [resendDisabled, setResendDisabled] = useState(false);
  const [otpIncorrect, setOtpIncorrect] = useState(false);
  const [otpFailPopup, setOtpFailPopup] = useState(false);
  const [showInputNoSecondLeftError, setShowInputNoSecondLeftError] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  const shouldRunPreloadEffect = secondsLeft !== undefined;

  const calculateSecondsLeft = useCallback((expiresAt: string) => {
    const expiryDate = new Date(expiresAt).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((expiryDate - now) / 1000));
  }, []);

  useEffect(() => {
    if (!requestOtpRes) return;
    const interval = setInterval(() => {
      const newSecondsLeft = calculateSecondsLeft(requestOtpRes?.expiresAt);
      setSecondsLeft(newSecondsLeft);

      if (newSecondsLeft <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [requestOtpRes?.expiresAt]);

  useEffect(() => {
    if (!requestOtpRes) return;
    const interval = setInterval(() => {
      const newSecondsLeft = calculateSecondsLeft(
        requestOtpRes?.nextResendAvailableAt
      );
      setResendSecondsLeft(newSecondsLeft);

      if (newSecondsLeft <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [requestOtpRes?.nextResendAvailableAt]);

  useEffect(() => {
    if (!requestOtpRes) return;

    const interval = setInterval(() => {
      const isResendDisabled =
        new Date() < new Date(requestOtpRes.nextResendAvailableAt);
      setResendDisabled(isResendDisabled);
    }, 1000);

    return () => clearInterval(interval);
  }, [requestOtpRes?.nextResendAvailableAt]);

  const onRequestOtp = useCallback(async () => {
    setShowInputNoSecondLeftError(false);
    setRequestedOTP(true);
    const payload = { countryCode, mobileNo };
    const onRequestOtpSuccess = (res: RequestOtpRes) => {
      setRequestOtpRes(res);
      setSid(res.sid);
    };
    const onRequestOtpError = () => setRequestedOTP(false);

    secSession
      ? requestOtpEnc(
          await constructSecurePayload(
            { lang: getApiLangParam(i18n.language), ...payload },
            secSession.pk
          ),
          onRequestOtpSuccess,
          onRequestOtpError
        )
      : requestOtp(payload, onRequestOtpSuccess, onRequestOtpError);
  }, [countryCode, mobileNo]);

  const onVerifyOtp = async (sid: string, otp: string) => {
    if (requestOtpRes?.resendQuota === 0 && !secondsLeft) {
      setOtpFailPopup(true);
      return;
    }
    if (!secondsLeft) {
      setShowInputNoSecondLeftError(true);
      return;
    }
    setOTPVerifying(true);
    const payload = { sid, otp };
    const onVerifyOtpSuccess = (res: VerifyOtpRes) => {
      const { sid, isSuccess } = res;
      setSubmitting(true);
      isSuccess && onSubmit(sid, () => setSubmitting(false));
    };
    const onVerifyOtpError = (err: KnownError | DOMException) => {
      if (isKnownError(err)) {
        if (err.errorCode === ERROR_CODE.INCORRECT_OTP) setOtpIncorrect(true);
        else if (err.errorCode === ERROR_CODE.EXPIRED_OTP) {
          requestOtpRes?.resendQuota === 0
            ? setOtpFailPopup(true)
            : setShowInputNoSecondLeftError(true);
        }
      }
    };

    secSession
      ? verifyOtpEnc(
          await constructSecurePayload(payload, secSession.pk),
          onVerifyOtpSuccess,
          onVerifyOtpError,
          () => setOTPVerifying(false)
        )
      : verifyOtp(payload, onVerifyOtpSuccess, onVerifyOtpError, () =>
          setOTPVerifying(false)
        );
  };

  useEffect(() => {
    setTimeout(() => window.scroll({ top: -1, left: 0 }), 10);

    document.body.className = "preload";
    const timer = setTimeout(
      () => document.body.classList.remove("preload"),
      300
    );

    return () => clearTimeout(timer);
  }, [shouldRunPreloadEffect]);

  return (
    <>
      <div
        className={`${styles.container} ${
          type === "create" ? styles.createMode : styles.editMode
        }`}
      >
        <StepIndicator
          value={type === "create" ? 3 : 2}
          total={3}
          className={styles.stepIndicator}
        />
        <span className={styles.pageTitle}>
          {type === "create"
            ? t("otpVerification")
            : t("editOrCancelAppointment")}
        </span>
        <div className={styles.content}>
          {!requestOtpRes || secondsLeft === undefined ? (
            <>
              {type === "edit" && (
                <div className={styles.header}>{t("otpVerification")}</div>
              )}
              <div className={styles.title}>{t("systemSendingOTP")}</div>
              <div
                className={styles.mobileNo}
              >{`(${countryCode}) ${mobileNo}`}</div>

              <div className={styles.buttons}>
                <Button
                  type="primary"
                  onClick={onRequestOtp}
                  loading={requestedOTP}
                >
                  <span>{t("sendOtp")}</span>
                </Button>
                <Button
                  type="secondary"
                  onClick={onBack}
                  disabled={requestedOTP}
                >
                  <span>{t("returnToEdit")}</span>
                </Button>
              </div>
            </>
          ) : (
            <>
              <BackButton
                disabled={otpVerifying || submitting}
                title={t("return")}
                style={{ left: "24px" }}
                onClick={() => {
                  setRequestedOTP(false);
                  setSecondsLeft(undefined);
                  setRequestOtpRes(undefined);
                  setOtp("");
                }}
              />
              <div className={styles.detail}>
                <span className={styles.infoTitle}>
                  {t("otpSent", {
                    mobileNo: `(${requestOtpRes.countryCode}) ${requestOtpRes.mobileNo}`,
                  })}
                </span>
                <div className={styles.input}>
                  <InputField
                    name="otp"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value);
                      setOtpIncorrect(false);
                    }}
                    validation
                    error={
                      showInputNoSecondLeftError
                        ? t("passwordExpired")
                        : otpIncorrect
                        ? t("passwordError")
                        : ""
                    }
                  />
                </div>
                <div className={styles.reminder}>
                  <span
                    className={`${styles.text} ${
                      secondsLeft ? "" : styles.otpExpired
                    }`}
                  >
                    {secondsLeft
                      ? getOtpExpireMessage(secondsLeft)
                      : t("passwordExpired")}
                  </span>

                  <button
                    className={styles.resend}
                    onClick={onRequestOtp}
                    disabled={otpVerifying || resendDisabled || submitting}
                  >
                    <IconSend
                      style={{ minWidth: "17.027px", minHeight: "15.048px" }}
                    />
                    <span>{t("resend")}</span>
                    {/* {!!resendSecondsLeft && <span>({formatTimeInMinutesSeconds(resendSecondsLeft)})</span>} */}
                  </button>
                </div>
                <div className={styles.button}>
                  <Button
                    type="primary"
                    disabled={otp?.length !== 6 || !secondsLeft}
                    onClick={() => onVerifyOtp(sid, otp!)}
                    loading={otpVerifying || submitting}
                  >
                    {t("confirm")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Popup
        isOpen={otpFailPopup}
        title={t("passwordSendFail")}
        content={t("passwordSendFailMessage")}
        status={POPUP_STATUS.ERROR}
        closeBtnProps={{ type: "secondary" }}
        closeTitle={t("changePhoneNumber")}
        onClose={onBack}
        confirmBtnProps={{ type: "secondary" }}
        confirmTitle={t("restartAppointment")}
        onConfirm={window.location.reload}
        verticalButtons
      />
    </>
  );
};

export default Validation;
